import Head from 'next/head';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { useSelector } from 'react-redux';
import SEO from 'next-seo.config';
import { fetchNews } from 'api/newsroom';
import { wrapper } from 'store';
import { authSelector } from 'store/auth';
import SidebarLayout from 'layouts/SidebarLayout';
import Typo from 'components/Typo';
import pageTitle from 'utils/seo-title';
import { alertPath } from 'utils/alert-url';
import { formatDate } from 'utils/newsroom';
import { jsonLd } from 'utils/safe-html';
import { authorPageSchema, authorPath } from 'utils/news-article-schema';
import AuthorBlock from 'components/Alert/Newsroom/AuthorBlock';
import styles from 'styles/modules/Raws.module.scss';

const PAGE_SIZE = 20;

const newsHref = (page, { profile, author } = {}) => {
    const base = author ? authorPath(author) : '/news';
    const query = new URLSearchParams({
        ...(profile ? { profile } : {}),
        ...(page > 1 ? { page } : {}),
    }).toString();
    return query ? `${base}?${query}` : base;
};

// Every published story, newest first: the one public page that links to them
// all, so a signed-out reader or a crawler can reach each story.
const News = ({ news, page }) => {
    const { isAuthenticated } = useSelector(authSelector);
    const items = news?.data ?? [];
    const totalPages = news?.totalPages ?? 0;
    // /news?profile=slug: the stories one expert is quoted in, linked from their
    // profile. Noindex, as it repeats /news; its links are still followed.
    const profile = news?.profile ?? null;
    // /authors/slug: the stories bylined to one author, under who they are.
    const author = news?.author ?? null;
    const filter = { profile: profile?.slug, author: author?.slug };
    const heading = author
        ? [author.name, !author.isOrganization && author.jobTitle]
              .filter(Boolean)
              .join(', ')
        : profile
        ? `Stories quoting ${profile.name}`
        : 'Latest news';

    return (
        <SidebarLayout isAuthenticated={isAuthenticated}>
            <NextSeo
                {...SEO}
                titleTemplate={pageTitle(
                    page > 1 ? `${heading}, page ${page}` : heading
                )}
                description={
                    author
                        ? author.bio ||
                          `Stories by ${author.name} for Newspage.`
                        : "The latest stories from the Newspage newsroom, written from our experts' answers. Newest first."
                }
                canonical={`${process.env.HOST}${newsHref(page, filter)}`}
                noindex={Boolean(profile)}
            />
            {author && (
                <Head>
                    <script
                        key="author-jsonld"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: jsonLd(
                                authorPageSchema(author, process.env.HOST)
                            ),
                        }}
                    />
                </Head>
            )}

            <div className={styles.wrapper}>
                {author ? (
                    <div className={styles.head}>
                        <AuthorBlock author={author} onPage />
                        <h2 className={styles.authorStories}>
                            Stories by {author.name}
                        </h2>
                    </div>
                ) : (
                    <div className={styles.head}>
                        <Typo variant={'h1'}>{heading}</Typo>
                        <p className={styles.intro}>
                            {profile ? (
                                <>
                                    Newspage stories quoting{' '}
                                    <Link href={`/${profile.slug}`}>
                                        <a>{profile.name}</a>
                                    </Link>
                                    . Newest first.
                                </>
                            ) : (
                                'Stories from the Newspage newsroom. Newest first.'
                            )}
                        </p>
                    </div>
                )}

                {items.length ? (
                    <ul className={styles.list}>
                        {items.map((story) => (
                            <li key={story.slug} className={styles.item}>
                                <p className={styles.itemMeta}>
                                    {`By ${story.byline || 'Newspage'} · `}
                                    <time dateTime={story.publishedAt}>
                                        {formatDate(story.publishedAt)}
                                    </time>
                                </p>
                                <h2 className={styles.itemTitle}>
                                    <Link href={alertPath(story.slug)}>
                                        <a>{story.headline}</a>
                                    </Link>
                                </h2>
                                {story.standfirst && (
                                    <p className={styles.excerpt}>
                                        {story.standfirst}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className={styles.empty}>No stories yet.</p>
                )}

                {totalPages > 1 && (
                    <nav className={styles.pager} aria-label="Pages">
                        {page > 1 ? (
                            <Link href={newsHref(page - 1, filter)}>
                                <a rel="prev">← Newer</a>
                            </Link>
                        ) : (
                            <span />
                        )}
                        <span>
                            Page {page} of {totalPages}
                        </span>
                        {page < totalPages ? (
                            <Link href={newsHref(page + 1, filter)}>
                                <a rel="next">Older →</a>
                            </Link>
                        ) : (
                            <span />
                        )}
                    </nav>
                )}
            </div>
        </SidebarLayout>
    );
};

const slugParam = (value) =>
    typeof value === 'string' && value ? value : undefined;

// The listing behind /news and /authors/[slug]; filterOf picks the stories from the query.
export const newsPageProps = (filterOf) =>
    wrapper.getServerSideProps(() => async ({ query: params }) => {
        const page = Math.max(1, parseInt(params.page, 10) || 1);
        const filter = filterOf(params);
        let news;
        try {
            news = await fetchNews({ page, size: PAGE_SIZE, ...filter });
        } catch (err) {
            // Any other failure is a 500, so a crawler never sees an empty listing as the page.
            if (
                (filter.profile || filter.author) &&
                err?.response?.status === 404
            ) {
                return { notFound: true };
            }
            throw err;
        }

        // A page past the end is not an empty listing to index.
        if (page > 1 && !news.data?.length) {
            return { notFound: true };
        }

        return { props: { news, page } };
    });

export const getServerSideProps = newsPageProps((params) => ({
    profile: slugParam(params.profile),
}));

// No wrapper.withRedux: _app.js already provides the store.
export default News;
